<template>
    <div class="genre-library overflow-y-auto h-screen-header" ref="genreview">
        <section class="genre-hero">
            <div class="genre-hero__copy">
                <div class="genre-hero__label">{{ $tt('流派收藏') }}</div>
                <h1>{{ $tt('按风格找到音乐的入口') }}</h1>
                <p>{{ $tt('把流派、专辑和歌曲规模放在同一个视野里，快速进入某一种声音。') }}</p>
                <div class="genre-hero__actions">
                    <button type="button" class="btn btn-primary btn-sm" :disabled="!items.length || loading" @click="playTopGenre">
                        {{ $tt('播放最大流派') }}
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" :disabled="loading" @click="refreshCurrentPage">
                        {{ $tt('刷新') }}
                    </button>
                </div>
            </div>

            <div class="genre-hero__stage" aria-hidden="true">
                <div v-if="heroGenres.length" class="genre-spectrum">
                    <div
                        v-for="(genre, index) in heroGenres"
                        :key="`${genre.id || genre.name}_${index}`"
                        class="genre-spectrum__item"
                        :style="{ '--genre-index': index, '--genre-weight': genreWeight(genre) }"
                    >
                        <strong>{{ genre.name }}</strong>
                        <span>{{ Number(genre.albumCount || 0) }} {{ $tt('专辑') }} / {{ Number(genre.trackCount || 0) }} {{ $tt('歌曲') }}</span>
                    </div>
                </div>
                <div v-else class="genre-spectrum__empty">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </section>

        <section class="genre-toolbar">
            <div class="genre-toolbar__tabs scrollbar-hide">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    type="button"
                    :class="['genre-tab', sortValue === value ? 'is-active' : '']"
                    @click="switchSort(value)"
                >
                    {{ $tt(text) }}
                </button>
            </div>
        </section>

        <section class="genre-status-strip">
            <div>
                <span class="genre-status-strip__label">{{ $tt('当前规则') }}</span>
                <strong>{{ currentSortLabel }}</strong>
            </div>
            <div>
                <span class="genre-status-strip__label">{{ $tt('已载入') }}</span>
                <strong>{{ items.length }}</strong>
            </div>
            <div>
                <span class="genre-status-strip__label">{{ $tt('专辑总数') }}</span>
                <strong>{{ totalAlbumCount }}</strong>
            </div>
            <div>
                <span class="genre-status-strip__label">{{ $tt('歌曲总数') }}</span>
                <strong>{{ totalTrackCount }}</strong>
            </div>
        </section>

        <section class="genre-content">
            <Tiles v-if="sortedItems.length">
                <div
                    v-for="(item, index) in sortedItems"
                    :key="item.id || item.name"
                    :class="index < 18 ? 'genre-tile-shell is-animated' : 'genre-tile-shell'"
                    :style="index < 18 ? { '--tile-index': index } : {}"
                >
                    <Tile
                        :to="{ name: 'genre', params: { id: item.id } }"
                        :title="item.name"
                        :image="item.image"
                        :square="false"
                        :small="false"
                        :allow-h-scroll="false"
                    >
                        <template #text>
                            <div class="genre-card-meta">
                                <span><strong>{{ Number(item.albumCount || 0) }}</strong> {{ $tt('专辑') }}</span>
                                <span><strong>{{ Number(item.trackCount || 0) }}</strong> {{ $tt('歌曲') }}</span>
                            </div>
                        </template>
                        <template #play>
                            <button type="button" class="genre-play-button" :title="$tt('播放流派')" @click.stop="playNow(item.id)">
                                <svg class="fill-current w-12 h-12" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M512 1024A512 512 0 1 1 512 0a512 512 0 0 1 0 1024zM383.232 287.616v448l384-223.104-384-224.896z"></path>
                                </svg>
                            </button>
                        </template>
                    </Tile>
                </div>
            </Tiles>

            <div v-if="loading" class="genre-loading">
                <div class="genre-loading__grid">
                    <span v-for="index in 8" :key="index"></span>
                </div>
                <p>{{ $tt('正在载入流派') }}</p>
            </div>

            <div v-if="!loading && items.length === 0" class="genre-empty">
                <div class="genre-empty__art">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <h3>{{ $tt('暂无流派') }}</h3>
                <p>{{ $tt('还没有找到任何流派，可以切换排序或刷新音乐库。') }}</p>
            </div>
        </section>

        <button v-if="curScrollTop > 700" @click="scrollToTop" class="genre-scroll-top" :title="$tt('返回顶部')">
            <svg class="icon h-5 w-5 fill-current" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                <path d="M890.5 755.3L537.9 269.2c-12.8-17.6-39-17.6-51.7 0L133.5 755.3c-3.8 5.3-0.1 12.7 6.5 12.7h75c5.1 0 9.9-2.5 12.9-6.6L512 369.8l284.1 391.6c3 4.1 7.8 6.6 12.9 6.6h75c6.5 0 10.3-7.4 6.5-12.7z"></path>
            </svg>
        </button>
    </div>
</template>

<script>
import Tiles from '@/components/library/Tiles'
import Tile from '@/components/library/Tile'

export default {
    name: 'genres',
    components: {
        Tiles,
        Tile
    },
    props: {
        sort: { type: String, default: null }
    },
    data() {
        return {
            loading: true,
            items: [],
            curScrollTop: 0,
            isScrollBound: false,
            options: [
                { text: 'A-Z排序', value: 'a-z' },
                { text: '最多专辑', value: 'most-albums' },
                { text: '最多歌曲', value: 'most-tracks' }
            ]
        }
    },
    computed: {
        sortedItems() {
            return this.items
        },
        sortValue() {
            return this.$route.params.sort || 'a-z'
        },
        currentSortLabel() {
            const option = this.options.find(item => item.value === this.sortValue)
            return option ? this.$tt(option.text) : this.$tt('A-Z排序')
        },
        totalAlbumCount() {
            return this.items.reduce((sum, item) => sum + Number(item.albumCount || 0), 0)
        },
        totalTrackCount() {
            return this.items.reduce((sum, item) => sum + Number(item.trackCount || 0), 0)
        },
        heroGenres() {
            return this.items.slice(0, 6)
        },
        largestGenre() {
            if (!this.items.length) {
                return null
            }
            return this.items.reduce((largest, item) => {
                return Number(item.trackCount || 0) > Number(largest.trackCount || 0) ? item : largest
            }, this.items[0])
        }
    },
    created() {
        this.loadGenres()
    },
    mounted() {
        this.bindScrollListener()
        this.restoreScroll()
    },
    activated() {
        this.bindScrollListener()
        this.restoreScroll()
    },
    deactivated() {
        this.unbindScrollListener()
    },
    beforeDestroy() {
        this.unbindScrollListener()
    },
    methods: {
        bindScrollListener() {
            if (this.$refs.genreview && !this.isScrollBound) {
                this.$refs.genreview.addEventListener('scroll', this.listenBottomOut)
                window.genreview = this.$refs.genreview
                this.isScrollBound = true
            }
        },
        unbindScrollListener() {
            if (this.$refs.genreview && this.isScrollBound) {
                this.$refs.genreview.removeEventListener('scroll', this.listenBottomOut, false)
                this.isScrollBound = false
            }
        },
        restoreScroll() {
            if (this.$refs.genreview) {
                this.$refs.genreview.scrollTop = this.curScrollTop || 0
            }
        },
        async loadGenres() {
            this.loading = true
            try {
                this.items = await this.$api.subsonic.getGenres(this.sortValue)
            } catch (err) {
                this.items = []
                if (this.$myMsg && err && err.message !== 'Wrong username or password.') {
                    this.$myMsg.notify({
                        title: this.$tt('载入失败'),
                        content: err.message || this.$tt('流派载入失败'),
                        type: 'error',
                        time: 5000
                    })
                }
            } finally {
                this.loading = false
            }
        },
        refreshCurrentPage() {
            return this.loadGenres()
        },
        switchSort(value) {
            if (this.sortValue === value) {
                this.refreshCurrentPage()
                return
            }
            this.$router.push({ path: '/genres/' + value })
        },
        async playTopGenre() {
            if (this.largestGenre) {
                return this.playNow(this.largestGenre.id)
            }
            return null
        },
        async playNow(id) {
            if (!id) {
                return null
            }
            const genre = await this.$api.subsonic.getTracksByGenre(id, 100)
            return this.$store.dispatch('playTrackList', {
                tracks: genre
            })
        },
        genreWeight(genre) {
            const tracks = Number(genre.trackCount || 0)
            if (!this.totalTrackCount) {
                return 0.28
            }
            return Math.min(0.86, Math.max(0.28, tracks / this.totalTrackCount * 3.6))
        },
        listenBottomOut() {
            const container = this.$refs.genreview || window.genreview
            this.curScrollTop = container ? container.scrollTop : document.body.scrollTop
        },
        scrollToTop() {
            if (this.$refs.genreview) {
                this.$refs.genreview.scrollTop = 0
            }
        }
    }
}
</script>

<style scoped>
.genre-library {
    --genre-radius: 18px;
    --genre-border: oklch(var(--bc) / 0.12);
    --genre-muted: oklch(var(--bc) / 0.62);
    --genre-soft: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    min-height: 0;
    overflow-x: hidden;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 8%, color-mix(in srgb, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 88% 4%, color-mix(in srgb, oklch(var(--a)) 12%, transparent), transparent 26rem),
        oklch(var(--b2));
    padding: 18px;
}

.genre-hero,
.genre-toolbar,
.genre-status-strip,
.genre-content {
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
}

.genre-hero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.12fr) minmax(300px, 0.88fr);
    gap: 28px;
    align-items: stretch;
    min-height: 226px;
    margin-bottom: 14px;
    border: 1px solid var(--genre-border);
    border-radius: var(--genre-radius);
    background:
        radial-gradient(circle at 86% 18%, color-mix(in oklch, oklch(var(--p)) 18%, transparent), transparent 24rem),
        linear-gradient(
            105deg,
            oklch(var(--b1) / 0.98) 0%,
            oklch(var(--b1) / 0.96) 42%,
            color-mix(in oklch, oklch(var(--b2)) 88%, oklch(var(--p)) 6%) 68%,
            color-mix(in oklch, oklch(var(--b3)) 84%, oklch(var(--p)) 10%) 100%
        ),
        oklch(var(--b1));
    box-shadow: 0 20px 60px oklch(var(--bc) / 0.08);
    isolation: isolate;
    overflow: hidden;
}

.genre-hero__copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: clamp(24px, 4vw, 44px);
}

.genre-hero__label {
    width: fit-content;
    margin-bottom: 14px;
    padding: 5px 10px;
    border-radius: 999px;
    background: oklch(var(--p) / 0.14);
    color: oklch(var(--p));
    font-size: 12px;
    font-weight: 700;
}

.genre-hero h1 {
    max-width: 680px;
    margin: 0;
    font-size: clamp(30px, 4vw, 54px);
    line-height: 1.04;
    font-weight: 800;
    letter-spacing: 0;
}

.genre-hero p {
    max-width: 55ch;
    margin-top: 16px;
    color: var(--genre-muted);
    font-size: 15px;
    line-height: 1.75;
}

.genre-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
}

.genre-hero__stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 226px;
    padding: 24px;
    background: transparent;
    isolation: isolate;
    overflow: hidden;
}

.genre-hero__stage::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        linear-gradient(
            90deg,
            transparent 0%,
            color-mix(in oklch, oklch(var(--b3)) 42%, transparent) 46%,
            color-mix(in oklch, oklch(var(--b3)) 68%, oklch(var(--p)) 8%) 100%
        );
    pointer-events: none;
}

.genre-spectrum {
    position: relative;
    z-index: 1;
    display: grid;
    width: min(520px, 100%);
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    align-items: end;
}

.genre-spectrum__item {
    display: flex;
    min-height: calc(88px + (96px * var(--genre-weight)));
    flex-direction: column;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 12px;
    border: 1px solid oklch(var(--bc) / 0.13);
    border-radius: 16px;
    background:
        linear-gradient(180deg, oklch(var(--b1) / 0.86), oklch(var(--b2) / 0.9)),
        oklch(var(--b1));
    box-shadow: 0 14px 34px oklch(var(--bc) / 0.13);
}

.genre-spectrum__item strong,
.genre-spectrum__item span {
    overflow: hidden;
    text-overflow: ellipsis;
}

.genre-spectrum__item strong {
    font-size: 14px;
    font-weight: 800;
    line-height: 1.2;
    white-space: nowrap;
}

.genre-spectrum__item span {
    color: var(--genre-muted);
    font-size: 11px;
    line-height: 1.35;
}

.genre-spectrum__empty {
    position: relative;
    z-index: 1;
    width: min(340px, 78vw);
    height: 150px;
}

.genre-spectrum__empty span {
    position: absolute;
    bottom: 0;
    width: 31%;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 16px;
    background: oklch(var(--b1) / 0.74);
}

.genre-spectrum__empty span:nth-child(1) {
    left: 0;
    height: 72px;
}

.genre-spectrum__empty span:nth-child(2) {
    left: 34.5%;
    height: 126px;
    background: oklch(var(--p) / 0.22);
}

.genre-spectrum__empty span:nth-child(3) {
    right: 0;
    height: 96px;
}

.genre-toolbar {
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding: 10px;
    border: 1px solid var(--genre-border);
    border-radius: var(--genre-radius);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(14px);
    z-index: 3;
}

.genre-toolbar__tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 1px;
}

.genre-tab {
    flex: 0 0 auto;
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: transparent;
    color: oklch(var(--bc) / 0.72);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.genre-tab:hover {
    background: oklch(var(--b2));
    color: oklch(var(--bc));
}

.genre-tab:active,
.genre-play-button:active,
.genre-scroll-top:active {
    transform: translateY(1px);
}

.genre-tab.is-active {
    background: oklch(var(--p));
    color: oklch(var(--pc));
    border-color: oklch(var(--p));
}

.genre-status-strip {
    display: grid;
    grid-template-columns: 1.25fr 1fr 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
}

.genre-status-strip > div {
    min-width: 0;
    padding: 14px 16px;
    border: 1px solid var(--genre-border);
    border-radius: 16px;
    background: var(--genre-soft);
}

.genre-status-strip__label {
    display: block;
    margin-bottom: 3px;
    color: var(--genre-muted);
    font-size: 12px;
}

.genre-status-strip strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 18px;
}

.genre-content {
    padding: 2px 0 28px;
}

.genre-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    color: oklch(var(--bc) / 0.68);
    font-size: 13px;
}

.genre-card-meta span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.genre-play-button {
    display: inline-flex;
    width: 54px;
    height: 54px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    color: oklch(var(--p));
    filter: drop-shadow(0 12px 24px oklch(var(--bc) / 0.28));
    transition: color 180ms ease, transform 180ms ease;
}

.genre-play-button:hover {
    color: oklch(var(--pc));
}

.genre-loading,
.genre-empty {
    display: flex;
    min-height: 260px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border: 1px solid var(--genre-border);
    border-radius: var(--genre-radius);
    background: oklch(var(--b1) / 0.74);
    text-align: center;
}

.genre-loading__grid {
    display: grid;
    width: min(500px, 78vw);
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.genre-loading__grid span {
    aspect-ratio: 1.35;
    border-radius: 16px;
    background: linear-gradient(110deg, oklch(var(--b2)), oklch(var(--b3)), oklch(var(--b2)));
    background-size: 200% 100%;
}

.genre-loading p,
.genre-empty p {
    max-width: 36ch;
    color: var(--genre-muted);
    font-size: 14px;
}

.genre-empty__art {
    display: grid;
    width: 132px;
    height: 96px;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    align-items: end;
}

.genre-empty__art span {
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 16px;
    background: oklch(var(--b2));
}

.genre-empty__art span:nth-child(1) {
    height: 62px;
}

.genre-empty__art span:nth-child(2) {
    height: 96px;
    background: oklch(var(--p) / 0.22);
}

.genre-empty__art span:nth-child(3) {
    height: 74px;
}

.genre-empty h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
}

.genre-scroll-top {
    position: fixed;
    right: 24px;
    bottom: 24px;
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: oklch(var(--p));
    color: oklch(var(--pc));
    box-shadow: 0 12px 30px oklch(var(--bc) / 0.2);
    transition: transform 180ms ease, background-color 180ms ease;
    z-index: 4;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

@media (prefers-reduced-motion: no-preference) {
    .genre-hero,
    .genre-toolbar,
    .genre-status-strip,
    .genre-content {
        animation: genreFadeIn 360ms ease both;
    }

    .genre-tile-shell.is-animated {
        animation: genreTileIn 420ms ease both;
        animation-delay: calc(var(--tile-index) * 42ms);
    }

    .genre-loading__grid span {
        animation: genreSkeleton 1.2s ease-in-out infinite;
    }
}

@keyframes genreFadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes genreTileIn {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes genreSkeleton {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

@media screen and (max-width: 1040px) {
    .genre-spectrum {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .genre-spectrum__item {
        min-height: calc(72px + (54px * var(--genre-weight)));
    }
}

@media screen and (max-width: 900px) {
    .genre-library {
        padding: 12px;
    }

    .genre-hero {
        grid-template-columns: 1fr;
    }

    .genre-hero__stage {
        min-height: 190px;
    }
}

@media screen and (max-width: 640px) {
    .genre-hero__copy {
        padding: 22px;
    }

    .genre-hero h1 {
        font-size: 30px;
    }

    .genre-status-strip {
        grid-template-columns: 1fr;
    }

    .genre-spectrum {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .genre-spectrum__item {
        min-height: 86px;
    }

    .genre-loading__grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .genre-scroll-top {
        right: 16px;
        bottom: 16px;
    }
}
</style>
